import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import {
  registrationSchema,
  type RegistrationFormInput,
  type RegistrationFormValues,
} from '@emazy/shared-validation';

import { buildVerifyEmailRoute, routes } from '@/config/routes';
import {
  FormErrorSummary,
  PasswordField,
  PasswordRequirements,
  SubmitButton,
} from '@/features/auth/components';
import { useAuth } from '@/features/auth/context';
import { getAuthErrorMessage, normalizeAuthError } from '@/features/auth/utils';
import { useDocumentMetadata } from '@/hooks/useDocumentMetadata';

export function RegisterPage() {
  useDocumentMetadata('Create Account');
  const navigate = useNavigate();
  const { register: createAccount } = useAuth();
  const [authError, setAuthError] = useState<string>();
  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm<RegistrationFormInput, unknown, RegistrationFormValues>({
    resolver: zodResolver(registrationSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
      acceptTerms: false,
      marketingOptIn: false,
    },
  });
  const password = useWatch({ control, name: 'password', defaultValue: '' });

  async function submit(values: RegistrationFormValues) {
    setAuthError(undefined);
    try {
      const result = await createAccount({
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        password: values.password,
        acceptTerms: values.acceptTerms,
        marketingOptIn: values.marketingOptIn,
      });
      navigate(buildVerifyEmailRoute({ email: result.email }), { state: { codeSent: true } });
    } catch (error) {
      setAuthError(getAuthErrorMessage(normalizeAuthError(error)));
    }
  }

  return (
    <div className="auth-page auth-page--wide">
      <p className="eyebrow">A place for your projects</p>
      <h1>Create your account</h1>
      <p className="auth-page__intro">
        Build the foundation for saved designs, order progress, and easier repeat ordering.
      </p>
      <FormErrorSummary message={authError} />
      <form className="auth-form" noValidate onSubmit={handleSubmit(submit)}>
        <div className="auth-form__grid">
          <label className="auth-field" htmlFor="first-name">
            <span>First name</span>
            <input
              id="first-name"
              className="p-inputtext p-component"
              autoComplete="given-name"
              {...register('firstName')}
              aria-invalid={Boolean(errors.firstName)}
              aria-describedby={errors.firstName ? 'first-name-error' : undefined}
            />
            {errors.firstName && (
              <small id="first-name-error" role="alert">
                {errors.firstName.message}
              </small>
            )}
          </label>
          <label className="auth-field" htmlFor="last-name">
            <span>Last name</span>
            <input
              id="last-name"
              className="p-inputtext p-component"
              autoComplete="family-name"
              {...register('lastName')}
              aria-invalid={Boolean(errors.lastName)}
              aria-describedby={errors.lastName ? 'last-name-error' : undefined}
            />
            {errors.lastName && (
              <small id="last-name-error" role="alert">
                {errors.lastName.message}
              </small>
            )}
          </label>
        </div>
        <label className="auth-field" htmlFor="register-email">
          <span>Email</span>
          <input
            id="register-email"
            className="p-inputtext p-component"
            type="email"
            autoComplete="email"
            {...register('email')}
            aria-invalid={Boolean(errors.email)}
            aria-describedby={errors.email ? 'register-email-error' : undefined}
          />
          {errors.email && (
            <small id="register-email-error" role="alert">
              {errors.email.message}
            </small>
          )}
        </label>
        <PasswordField
          id="register-password"
          label="Password"
          autoComplete="new-password"
          registration={register('password')}
          error={errors.password?.message}
        />
        <PasswordRequirements password={password} />
        <PasswordField
          id="confirm-password"
          label="Confirm password"
          autoComplete="new-password"
          registration={register('confirmPassword')}
          error={errors.confirmPassword?.message}
        />
        <label className="auth-checkbox auth-checkbox--legal">
          <input
            type="checkbox"
            {...register('acceptTerms')}
            aria-invalid={Boolean(errors.acceptTerms)}
            aria-describedby={errors.acceptTerms ? 'accept-terms-error' : undefined}
          />
          <span>
            I accept the <Link to={routes.terms}>Terms of Use</Link> and{' '}
            <Link to={routes.privacy}>Privacy Policy</Link>.
          </span>
        </label>
        {errors.acceptTerms && (
          <small className="field-error" id="accept-terms-error" role="alert">
            {errors.acceptTerms.message}
          </small>
        )}
        <label className="auth-checkbox">
          <input type="checkbox" {...register('marketingOptIn')} />
          <span>Send me occasional product ideas and Emazy updates. (Optional)</span>
        </label>
        <SubmitButton label="Create account" loading={isSubmitting} />
      </form>
      <p className="auth-page__alternate">
        Already have an account? <Link to={routes.login}>Sign in</Link>
      </p>
    </div>
  );
}
